import { createApi } from '@reduxjs/toolkit/query/react';
import {
  mergeConnectedDevices,
  mergeDataPointsIntoDevices,
} from 'features/Integrations/utils/integrationData.utils';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';
import { setDeviceDataStaleAfterMs } from 'utils/devices/staleness.utils';

const DEVICE_INTEGRATIONS_URL = '/v2/integrations/device';

/** The backend refuses a range request naming more devices than this (`max-ids-per-request`). */
export const DEVICE_DATA_MAX_IDS_PER_REQUEST = 25;
/** Points per device per page when a caller does not say. The backend default is 500, max 5000. */
export const DEVICE_DATA_DEFAULT_LIMIT = 500;

const getDeviceId = (device = {}) => device.id;

const RequestOnlyFields = (device = {}) => {
  const { id, ...payload } = device;
  return payload;
};

const toIdsParam = (ids) => (ids ? (Array.isArray(ids) ? ids.join(',') : ids) : undefined);

export const devicesApi = createApi({
  reducerPath: 'devicesApi', // This is the name of the slice in the store
  baseQuery: axiosBaseQuery(), // Custom axios base query (calls apiClient with the query url and params)
  tagTypes: ['Device', 'DeviceTypes', 'DeviceTypeMap'],
  endpoints: (builder) => ({
    getConnectedDevices: builder.query({
      // Fetches only the device list immediately so consumers get devices fast.
      // Each device's NEWEST data point is merged in asynchronously via onCacheEntryAdded
      // (getDeviceLatestData) — one row per device, so status/icons/map pins render on
      // first paint. A SERIES is fetched only for named devices, on demand, through
      // getNodeDataByTimestamps — it patches this cache entry.
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const devicesRes = await fetchWithBQ({ url: DEVICE_INTEGRATIONS_URL });
          if (devicesRes.error) {
            console.error('Error fetching connected devices:', devicesRes.error);
            return { error: devicesRes.error };
          }
          return { data: devicesRes.data?.result ?? [] };
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: err?.message ?? 'Unknown error in getConnectedDevices query',
            },
          };
        }
      },
      // A REFETCH must not throw the datapoints away. The list endpoint sends
      // every device with `data: []` — the points arrive separately (below) and
      // live only in this cache. `onCacheEntryAdded` runs once, when the entry
      // is first created, NOT on the refetch an `invalidatesTags: ['Device']`
      // causes; before this, that refetch replaced the cached devices with
      // point-less ones and every graph emptied until a page reload (Mike,
      // 2026-08-28, after saving flood settings). `merge` runs on every
      // response for an existing entry: keep each device's prior points, keyed
      // by uid and merged by timestamp, under the freshly fetched properties.
      merge: (currentCache, incoming) => mergeConnectedDevices(incoming, currentCache),
      // After the device list lands, fetch the newest row of every device and merge it
      // in. This is the ONLY device-data call the page load makes. It used to be a
      // 24-hour fetch for every device with no ids, which the backend expanded to the
      // whole workplace's history — that is what filled a gigabyte and crashed the
      // server (Mike, 2026-09-04). See
      // Fusion-Backend/Docs/issues/planning/device-data-on-demand/CONTRACT.md.
      async onCacheEntryAdded(
        _arg,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded;
        const initiateResult = dispatch(devicesApi.endpoints.getDeviceLatestData.initiate());
        try {
          const { data } = await initiateResult;
          if (data?.result) {
            updateCachedData((draft) => mergeDataPointsIntoDevices(draft, data.result));
          }
        } catch (err) {
          console.error('Error merging latest device data into getConnectedDevices cache:', err);
        } finally {
          await cacheEntryRemoved;
          initiateResult.unsubscribe?.();
        }
      },
      providesTags: ['Device'],
    }),
    // Fetch devices by an array of IDs
    getDevicesByIds: builder.query({
      query: (ids) => ({
        url: DEVICE_INTEGRATIONS_URL,
        method: 'GET',
        params: { ids: toIdsParam(ids) },
      }),
      transformResponse: (response) => response?.result ?? [],
    }),

    // The newest data point for each device, whatever its age — one row each. `ids`
    // optional; absent means every device in the workplace. `meta.staleAfterMs` is the
    // backend-owned threshold past which a TELEMETRY device's row reads Offline
    // (event-only types are exempt — see utils/devices/staleness.utils.js).
    getDeviceLatestData: builder.query({
      query: ({ ids } = {}) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/data/latest`,
        params: { ids: toIdsParam(ids) },
      }),
      transformResponse: (response) => ({
        result: response?.result ?? [],
        meta: response?.meta ?? {},
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          setDeviceDataStaleAfterMs(data?.meta?.staleAfterMs);
          dispatch(
            devicesApi.util.updateQueryData('getConnectedDevices', undefined, (draft) =>
              mergeDataPointsIntoDevices(draft, data?.result ?? []),
            ),
          );
        } catch {
          // Latest-data fetch failed — leave existing cache intact
        }
      },
    }),

    // One PAGE of data points for NAMED devices in a time range, newest first, merged
    // into the getConnectedDevices cache. `ids` is required (max 25) — a call without
    // ids does not go to the network. `limit`/`offset` page PER DEVICE. The resolved
    // value is `{ result, page }`; `page.hasMore` says whether to ask for the next
    // offset. Call it (via useLazyGetNodeDataByTimestampsQuery) when the user selects
    // a device to graph, changes the time range, opens a datasheet, or exports.
    getNodeDataByTimestamps: builder.query({
      async queryFn(
        { startTime, endTime, ids, limit, offset } = {},
        _queryApi,
        _extra,
        fetchWithBQ,
      ) {
        const idList = Array.isArray(ids) ? ids : ids != null ? [ids] : [];
        if (idList.length === 0) {
          return { data: { result: [], page: null } };
        }
        if (idList.length > DEVICE_DATA_MAX_IDS_PER_REQUEST) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: `Too many device ids. Maximum is ${DEVICE_DATA_MAX_IDS_PER_REQUEST}.`,
            },
          };
        }
        const res = await fetchWithBQ({
          url: `${DEVICE_INTEGRATIONS_URL}/data`,
          params: {
            startTime,
            endTime,
            ids: idList.join(','),
            limit: limit ?? DEVICE_DATA_DEFAULT_LIMIT,
            offset: offset ?? 0,
          },
        });
        if (res.error) return { error: res.error };
        return {
          data: {
            result: res.data?.result ?? [],
            page: res.data?.page ?? null,
          },
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (!data?.result?.length) return;
          dispatch(
            devicesApi.util.updateQueryData('getConnectedDevices', undefined, (draft) =>
              mergeDataPointsIntoDevices(draft, data.result),
            ),
          );
        } catch {
          // Node data fetch failed — leave existing cache intact
        }
      },
    }),
    addConnectedDevice: builder.mutation({
      query: (device) => ({
        url: DEVICE_INTEGRATIONS_URL,
        method: 'POST',
        data: { device },
      }),
    }),
    updateConnectedDevice: builder.mutation({
      query: (device) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/${encodeURIComponent(getDeviceId(device))}/properties`,
        method: 'PUT',
        data: { device: RequestOnlyFields(device) },
      }),
    }),
    removeConnectedDevice: builder.mutation({
      query: (device) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/${encodeURIComponent(getDeviceId(device))}`,
        method: 'DELETE',
      }),
    }),

    // --- Camera to siren association -------------------------------------
    //
    // `:id` is the CAMERA. Nothing could write this link before, so it had to
    // be made with hand-written SQL — and that single row is what decides
    // whether a siren shows its VMS controls, its "Manage Audio Files" button
    // and its Audio Manager (which holds BOTH upload and record) at all.
    //
    // These invalidate 'Device' rather than something narrower because BOTH
    // devices change shape: the siren gains `camera`, the camera gains its
    // siren.
    getDeviceSirenLink: builder.query({
      query: (cameraId) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/${encodeURIComponent(cameraId)}/siren`,
      }),
      transformResponse: (response) => response?.result ?? null,
      providesTags: ['Device'],
    }),
    setDeviceSirenLink: builder.mutation({
      query: ({ cameraId, sirenUid }) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/${encodeURIComponent(cameraId)}/siren`,
        method: 'PUT',
        data: { siren_uid: sirenUid },
      }),
      invalidatesTags: ['Device'],
    }),
    deleteDeviceSirenLink: builder.mutation({
      query: (cameraId) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/${encodeURIComponent(cameraId)}/siren`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Device'],
    }),

    getAllAvailableDeviceTypes: builder.query({
      query: () => ({
        url: '/v2/integrations/system/available-device-types',
      }),
      providesTags: ['DeviceTypes'],
      transformResponse: (response) => response.result || {}, // Extract the result from the response
    }),

    // New endpoint: getDeviceTypeMap
    getDeviceTypeMap: builder.query({
      query: () => ({
        url: `${DEVICE_INTEGRATIONS_URL}/map`,
      }),
      providesTags: ['DeviceTypeMap'],
      transformResponse: (response) => response.result || [], // Extract the result from the response
    }),
    saveTempAudio: builder.mutation({
      query: ({ toDelete = false, override = false }) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/audio/commit`,
        method: 'POST',
        data: { toDelete, override },
      }),
      invalidatesTags: ['DeviceTypes'],
    }),
    updateAudio: builder.mutation({
      query: ({ audioId, deviceIds }) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/audio/${encodeURIComponent(audioId)}/devices`,
        method: 'PUT',
        data: { deviceIds },
      }),
      invalidatesTags: ['DeviceTypes'],
    }),
    deleteAudio: builder.mutation({
      query: (audioIds) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/audio/${encodeURIComponent(audioIds)}`,
        method: 'DELETE',
        data: { audioIds },
      }),
      invalidatesTags: ['DeviceTypes'],
    }),
    editAudio: builder.mutation({
      query: ({ audioId, fileName }) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/audio/${encodeURIComponent(audioId)}`,
        method: 'PATCH',
        data: { fileName },
      }),
      invalidatesTags: ['DeviceTypes'],
    }),
    changeFloodStageDeviceSettings: builder.mutation({
      query: ({ deviceId, deviceSettings }) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/stage/${deviceId}/settings`,
        method: 'PATCH',
        data: deviceSettings,
      }),
      // The settings modal seeds its form from the cached device list. Without
      // this, a saved sim block stayed stale in that cache and the modal
      // reopened on the previous settings — Mike, 2026-08-28: "it seems to
      // reset".
      invalidatesTags: ['Device'],
    }),
    // The simulator's scenario catalogue, from the backend's one source: every
    // scenario, a summary, and each parameter's description and default.
    getStageSimScenarios: builder.query({
      query: () => ({
        url: `${DEVICE_INTEGRATIONS_URL}/stage/sim/scenarios`,
        method: 'GET',
      }),
      transformResponse: (response) => response?.scenarios ?? [],
    }),
    killswitchDevice: builder.mutation({
      query: ({ id, state }) => ({
        url: `${DEVICE_INTEGRATIONS_URL}/${encodeURIComponent(id)}/maintenance/killswitch`,
        method: 'POST',
        data: { state: Number(state) },
      }),
    }),
  }),
});

export const {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
  useGetDeviceTypeMapQuery,
  useGetDeviceLatestDataQuery,
  useLazyGetDeviceLatestDataQuery,
  useGetNodeDataByTimestampsQuery,
  useLazyGetNodeDataByTimestampsQuery,
  useAddConnectedDeviceMutation,
  useUpdateConnectedDeviceMutation,
  useRemoveConnectedDeviceMutation,
  useChangeFloodStageDeviceSettingsMutation,
  useGetStageSimScenariosQuery,
  useKillswitchDeviceMutation,
  useSaveTempAudioMutation,
  useUpdateAudioMutation,
  useDeleteAudioMutation,
  useEditAudioMutation,
  useGetDevicesByIdsQuery,
  useGetDeviceSirenLinkQuery,
  useSetDeviceSirenLinkMutation,
  useDeleteDeviceSirenLinkMutation,
} = devicesApi;
